import React, { useState, useEffect, useRef, useMemo } from "react"

import Badge from "./Badge"
import Sonar from "./Sonar"
import Hive from "./Hive"
import Tip from "./Tip"
import { getMissionActions } from "../services/api/v2/action"

import { isOverdue } from "../utils/actionItem"
import { getTaskSectionId } from "../utils/sections"
import { findAiEndDate } from "../utils/dates"
import { getRefId, getObjectId } from "../utils/utils"
import cx from "classnames"
import ProgressBar from "./ProgressBar"
import { addParam } from "./History"
import { isSameDay } from "date-fns/isSameDay"
import formatDistance from "date-fns/formatDistance"
import isTomorrow from "date-fns/isTomorrow"
import IconOhCheck from "./icons/IconOhCheck"

import {
    PiClockCountdownDuotone,
    PiCheckCircleDuotone,
    PiBellSimpleRingingDuotone,
    PiCarrotDuotone,
    PiAlarmDuotone,
    PiRadioactiveDuotone,
    PiChatCircleDotsDuotone,
    PiTriangleDuotone,
} from "react-icons/pi"

import format from "date-fns/format"

import apiNotification from "../services/notification/notification.api"
import { useNotifications, selectTaskMessages, shallowEqualArray } from "../services/notification/notificationStore"

import "./ActionItemTile.scss"

const ActionItemTile = ({ actionItem, orgData, missionData, onClick, app, showDuration, doneCallBack }) => {
    const [fadeAway, setFadeAway] = useState(false)
    const [imOverIt, setImOverIt] = useState(false)
    const [action, setAction] = useState({})

    const element = useRef(null)
    const scene = useRef(null)

    useEffect(() => {
        const getAction = async () => {
            const a = await getMissionActions(missionData?._id, {
                id: actionItem.actionId,
                select: ["title"],
            })

            setAction(a.value[0])
        }
        if (missionData._id) {
            getAction()
        }
    }, [])

    const onClickHandler = (e) => {
        if (onClick) {
            onClick()
            e.stopPropagation()
            return
        }

        addParam("action-item", actionItem._id)
    }

    const theme = missionData.theme || {}

    const cext = cx("action-item-external-wrapper", {
        "action-item-fade-away": fadeAway,
    })

    const cn = cx("dna-action-item-card", actionItem.className, {
        "dna-action-done": actionItem.status === "done",
        "action-item-overdue": isOverdue(actionItem),
    })
    const c = cx("tile-mission-img", "clip-" + Math.floor(Math.random() * 7))

    const people = useMemo(() => {
        if (!orgData?.people?.length) {
            return []
        }
        return actionItem?.people?.map((id) => {
            const person = orgData?.people.find((p) => getRefId(p) === id)

            return person
        })
    }, [orgData?.people, actionItem?.people])

    let title = actionItem.title || "No title specified..."

    const myMsgs = useNotifications(selectTaskMessages(actionItem._id), shallowEqualArray)

    const myUnread = myMsgs.filter((n) => !n.isRead)

    const progressWidth =
        actionItem.percentComplete || (actionItem.checklist && actionItem.checklist.length)
            ? (actionItem.checklist.filter((c) => c.done).length / actionItem.checklist.length) * 100
            : 0

    let tileHeader

    if (missionData.projectType === "simple" || missionData.projectType === "kanban") {
        // Is the task in a section (as before: true, not the section's title)
        tileHeader = getTaskSectionId(missionData, actionItem) ? true : undefined
    } else if ((missionData.projectType === "power" || missionData.projectType === "mx-gantt") && action) {
        tileHeader = action.title
    }

    return (
        <div
            className={cext}
            ref={element}
            onMouseEnter={() => {
                setImOverIt(true)
            }}
            onMouseLeave={() => {
                setImOverIt(false)
            }}
        >
            <div
                className={cn}
                onClick={(e) => {
                    if (myUnread.length) {
                        let idArray = []
                        myUnread.forEach((mu, i) => {
                            idArray.push(mu._id)
                        })

                        apiNotification
                            .markNotificationAsRead(idArray)
                            .then((res) => {})
                            .catch((err) => {})
                    }

                    onClickHandler(e)
                }}
            >
                <div className="dna-tile-content" ref={scene}>
                    {tileHeader && <label className="dna-action-title-label">{tileHeader}</label>}

                    <h3 id={"ai-tile-title-" + actionItem._id} className="dna-action-item-title x-clamp-1">
                        {title}
                    </h3>

                    {imOverIt && missionData && missionData.projectType !== "kanban" && (
                        <div
                            className="dna-center action-item-finish-this fake-click"
                            onClick={(e) => {
                                e.stopPropagation()

                                app.doneBurst.tune({ x: e.pageX, y: e.pageY }).generate().replay()

                                app.actionItemUpdateX(
                                    actionItem,
                                    {
                                        status: "done",
                                        percentComplete: 100,
                                    },
                                    () => {
                                        if (doneCallBack) {
                                            setTimeout(() => {
                                                doneCallBack()
                                            }, 400)
                                        }
                                    },
                                )
                            }}
                        >
                            <Tip title="Done?">
                                <IconOhCheck />
                            </Tip>
                        </div>
                    )}

                    {myUnread.length > 0 && (
                        <div className="tile-msgs">
                            <Badge badgeContent={myUnread.length}>
                                <PiChatCircleDotsDuotone />
                            </Badge>
                        </div>
                    )}
                    {actionItem.metaType === 0 && (
                        <Tip title="Critical task 🔥" red className={"ai-nuke active"}>
                            <Sonar color="silver">
                                <PiRadioactiveDuotone />
                            </Sonar>
                        </Tip>
                    )}

                    <div className={c} style={{ backgroundImage: theme.backgroundImage?.replace(".jpg", "-tile.jpg") }}>
                        <Tip title={"Jump to mission: " + missionData.title} position="top">
                            <label
                                onClick={(e) => {
                                    e.stopPropagation()

                                    addParam("mission", missionData._id)
                                }}
                                className="tile-mission-title"
                            >
                                {missionData.title}
                            </label>
                        </Tip>
                    </div>

                    <Hive
                        app={app}
                        width={30}
                        ai={actionItem}
                        people={people}
                        orgData={orgData}
                        mission={missionData}
                        offset={1}
                        maxLength={6}
                    />

                    <Footer ai={actionItem} mission={missionData} />
                    {showDuration && (
                        <ProgressBar color={isOverdue(actionItem) ? "#FD2A7D" : undefined} width={progressWidth} />
                    )}
                </div>
            </div>
        </div>
    )
}

const Footer = ({ ai, mission }) => {
    const myDueDate = findAiEndDate(ai)

    if (myDueDate && isNaN(Date.parse(myDueDate))) {
        const x = {
            myDueDate: myDueDate,
            _id: ai._id,
            title: ai.title,
            startDate: ai.startDate,
            week: ai.week,
            endDate: ai.endDate,
            estimatedDuration: ai.estimatedDuration,
            dueDate: ai.dueDate,
            mission: mission._id,
            missionTitle: mission.title,
            type: mission.isModel ? "model" : mission.isProposal ? "proposal" : "project",
        }

        throw Error(myDueDate?.toString() + " aiId: " + JSON.stringify(x))
    }

    const overdue = isOverdue(ai)

    const today = isSameDay(new Date(), myDueDate)
    const tomorrow = isTomorrow(myDueDate)

    return (
        <div className="ai-tile-footer">
            {overdue && !ai.endDate && myDueDate ? (
                <div>
                    <PiTriangleDuotone color="orange" /> Expected {formatDistance(new Date(), myDueDate)} ago
                </div>
            ) : overdue ? (
                <div>
                    <PiClockCountdownDuotone color="red" /> Overdue {formatDistance(new Date(), myDueDate)}
                </div>
            ) : today ? (
                <div>
                    <PiBellSimpleRingingDuotone color="orange" /> {ai.endDate ? "Due today" : "Estimated for today"}
                </div>
            ) : tomorrow ? (
                <div>
                    <PiAlarmDuotone color="gold" /> Due tomorrow
                </div>
            ) : myDueDate ? (
                <div>
                    <PiCarrotDuotone color="orange" /> Due on {format(myDueDate, "eee MMM d")}
                </div>
            ) : (
                <>{myDueDate?.toString()}</>
            )}
        </div>
    )
}

ActionItemTile.displayName = "ActionItemTile"
export default ActionItemTile
